import type { ContractStatus, DeliveryStatus, QuotationStatus, Role } from './types';

export type Tone = 'ok' | 'info' | 'warn' | 'bad' | 'neutral';

export function money(value: number | null | undefined, currency = 'USD'): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const code = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: code, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${code} ${n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`;
  }
}

export function num(value: number | null | undefined, digits = 2): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('zh-CN', { maximumFractionDigits: digits });
}

/**
 * 列表与详情里展示的日期：`26-10-29`（年-月-日，各两位）。
 *
 * 2026-10-29 现场要求统一为这个格式。改在这里而不是各页各写，是因为
 * `dateLabel` 被 30 个页面、94 处共用 —— 分散改必然出现两三种写法。
 *
 * 为什么用两位年：列表里日期列只承担「排序与扫读」的职责，两位年足够区分
 * （业务跨度在 10 年内），且比 `2026-10-29` 省 2 个字符 —— 在 10 列宽的表格里
 * 这 2 个字符直接决定相邻的金额/来源列能否露出来。
 *
 * 输入仍按 ISO（YYYY-MM-DD）解析，只是**输出**换成两位年；
 * 无法识别的一律回退原值，不静默吞掉异常数据。
 */
export function dateLabel(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s || '—';
  const [, y, mo, d] = m;
  return `${y.slice(2)}-${mo}-${d}`;
}

/** 需要完整四位年份的场合（打印件、对外单据、审计导出）用这个，不要用 dateLabel。 */
export function dateLabelFull(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : s || '—';
}

/**
 * 清洗从 ERPNext 导入时混进纯文本字段的 HTML 片段。
 *
 * 背景（2026-10-29 现场）：某条 BOM 的备注显示成 `<div><p>eje de bomba centrifuga</p></div>`。
 * 根因是 ERPNext 的富文本字段在迁移时被原样写进了纯文本列 —— 数据层存的是标签，
 * 前端按纯文本渲染就把标签也显示出来了。
 *
 * 只做两件事：去掉标签、合并多余空白；**不**做 HTML 解码（避免把 &lt; 之类
 * 还原成可执行标记），也**不**用 dangerouslySetInnerHTML —— 备注是用户输入，
 * 绝不能让它在页面上被当成 HTML 执行。
 *
 * 数据层的那一条也会单独清洗，这里只是防御：将来再导入脏数据时不至于又显示标签。
 */
export function plainText(value: string | null | undefined): string {
  const raw = String(value ?? '');
  if (!raw) return '';
  if (!/[<>]/.test(raw)) return raw;
  const stripped = raw
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|tr)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped;
}

/**
 * 列表里的客户名：只取前两个词，其余用省略号。
 *
 * 2026-10-29 现场要求：「客户名称不需要全部展示，通常前面两个词就可以了」。
 * 这比按字符数截断更贴合实际——西语公司名前面往往是辨识度最高的部分
 * （PETROCICAL SOCIEDAD…、FLOWBACK SERVICES…、VALMETAL S. A. S.），
 * 按词截断不会把词切一半，也不会因为后缀长短不一导致列宽抖动。
 *
 * 只用于**列表**。详情页、打印件、对外单据仍用完整名称（caller 传原值）。
 * 完整名称放进 title，悬停即可看到，信息没有丢失。
 */
export function customerShort(name: string | null | undefined, words = 2): string {
  const text = String(name ?? '').trim().replace(/\s+/g, ' ');
  if (!text) return '—';
  const parts = text.split(' ');
  if (parts.length <= words) return text;
  return `${parts.slice(0, words).join(' ')}…`;
}

const QUOTATION: Record<QuotationStatus, { label: string; tone: Tone }> = {
  draft: { label: '草稿', tone: 'neutral' },
  sent: { label: '已发送', tone: 'info' },
  accepted: { label: '已接受', tone: 'ok' },
  rejected: { label: '已拒绝', tone: 'bad' },
  expired: { label: '已过期', tone: 'neutral' },
  cancelled: { label: '已取消', tone: 'bad' },
};

const CONTRACT: Record<ContractStatus, { label: string; tone: Tone }> = {
  draft: { label: '草稿', tone: 'neutral' },
  confirmed: { label: '已确认', tone: 'info' },
  in_production: { label: '生产中', tone: 'warn' },
  ready: { label: '待发货', tone: 'info' },
  partially_delivered: { label: '部分交货', tone: 'warn' },
  completed: { label: '已完成', tone: 'ok' },
  cancelled: { label: '已取消', tone: 'bad' },
};

const DELIVERY: Record<DeliveryStatus, { label: string; tone: Tone }> = {
  draft: { label: '草稿', tone: 'neutral' },
  confirmed: { label: '已确认', tone: 'info' },
  dispatched: { label: '已发出', tone: 'warn' },
  delivered: { label: '已送达', tone: 'ok' },
  cancelled: { label: '已取消', tone: 'bad' },
};

export function quotationStatus(s: string): { label: string; tone: Tone } {
  return QUOTATION[s as QuotationStatus] || { label: s || '—', tone: 'neutral' };
}
export function contractStatus(s: string): { label: string; tone: Tone } {
  return CONTRACT[s as ContractStatus] || { label: s || '—', tone: 'neutral' };
}
export function deliveryStatus(s: string): { label: string; tone: Tone } {
  return DELIVERY[s as DeliveryStatus] || { label: s || '—', tone: 'neutral' };
}

export function workOrderStatus(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'draft': return { label: '草稿', tone: 'neutral' };
    case 'released': return { label: '已下达', tone: 'info' };
    case 'in_progress': return { label: '生产中', tone: 'warn' };
    case 'completed': return { label: '已完工', tone: 'ok' };
    case 'cancelled': return { label: '已取消', tone: 'bad' };
    // 作废 ≠ 取消：作废是终态且带留痕（人/时/因），只能由管理员执行，用于清理
    // 没有任何业务事实的误建工单；取消则是业务流程里的一环。
    case 'voided': return { label: '已作废', tone: 'bad' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}

// 工艺模板 / 材料方案审批状态（P6-5 收口）：draft → approved → deactivated。
export function planStatus(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'draft': return { label: '草稿', tone: 'neutral' };
    case 'approved': return { label: '已批准', tone: 'ok' };
    case 'deactivated': return { label: '已停用', tone: 'bad' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}

export function stockMovementType(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'receipt': return { label: '采购入库', tone: 'ok' };
    case 'issue': return { label: '领料出库', tone: 'warn' };
    case 'transfer': return { label: '调拨', tone: 'info' };
    case 'adjustment': return { label: '盘点调整', tone: 'neutral' };
    case 'delivery': return { label: '交货出库', tone: 'warn' };
    case 'customer_return': return { label: '客户退货入库', tone: 'ok' };
    case 'supplier_return': return { label: '供应商退料', tone: 'bad' };
    case 'production_consume': return { label: '生产领料', tone: 'warn' };
    case 'production_return': return { label: '生产退料', tone: 'info' };
    case 'production_output': return { label: '完工入库', tone: 'ok' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}

// 客户送修工件状态与历史场景、交还方式标签。
export function customerSuppliedMaterialStatus(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'received': return { label: '已收货', tone: 'info' };
    case 'in_process': return { label: '维修中', tone: 'warn' };
    case 'awaiting_return': return { label: '待交还', tone: 'warn' };
    case 'returned': return { label: '已交还', tone: 'ok' };
    case 'scrapped': return { label: '历史报废记录', tone: 'bad' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}
export function customerSuppliedScenario(s: string): string {
  switch (s) {
    case 'repair': return '维修';
    case 'processing': return '委外加工';
    case 'other': return '其他';
    default: return s || '—';
  }
}
export function customerMaterialReturnHandling(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'repaired': return { label: '已修复归还', tone: 'ok' };
    case 'processed': return { label: '加工完成归还', tone: 'ok' };
    case 'scrapped': return { label: '报废处理', tone: 'bad' };
    case 'partially_returned': return { label: '部分归还', tone: 'warn' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}

// 工件主档 / 维修版本标签（阶段6 · P6-2，与 core-api physical_assets / asset_service_cycles CHECK 对齐）。
export function physicalAssetStatus(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'active': return { label: '在库/正常', tone: 'neutral' };
    case 'in_service': return { label: '在厂维修', tone: 'warn' };
    case 'delivered': return { label: '已交付', tone: 'ok' };
    case 'retired': return { label: '已停用', tone: 'neutral' };
    case 'scrapped': return { label: '已报废', tone: 'bad' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}
export function serviceCycleStatus(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'intake': return { label: '已接收', tone: 'info' };
    case 'in_production': return { label: '生产中', tone: 'warn' };
    case 'inspected': return { label: '已检验', tone: 'warn' };
    case 'ready': return { label: '待交付', tone: 'info' };
    case 'delivered': return { label: '已交付', tone: 'ok' };
    case 'returned': return { label: '已归还', tone: 'ok' };
    case 'closed': return { label: '已关闭', tone: 'neutral' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}
export function serviceCycleScenario(s: string): string {
  switch (s) {
    case 'repair': return '维修';
    case 'processing': return '委外加工';
    case 'inspection_only': return '仅检测';
    case 'other': return '其他';
    default: return s || '—';
  }
}

// 采购订单状态 / 采购类型 / 费用类别标签（阶段3 · P3-1，与 core-api CHECK 约束对齐）。
export function purchaseStatus(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'draft': return { label: '草稿', tone: 'neutral' };
    case 'confirmed': return { label: '已确认', tone: 'info' };
    case 'partially_received': return { label: '部分收货', tone: 'warn' };
    case 'received': return { label: '已收货', tone: 'ok' };
    case 'cancelled': return { label: '已取消', tone: 'bad' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}
export function purchaseType(s: string): { label: string; tone: Tone } {
  switch (s) {
    // 列表里只需要「进口 / 国内」这个二元区分（2026-10-29 现场要求）。
    // 原「进口采购 / 国内采购」多两个字，在 9 列的采购订单表里纯粹占宽；
    // 详情页与表单仍用完整说法（那里有空间，也需要更明确）。
    case 'domestic': return { label: '国内', tone: 'info' };
    case 'import': return { label: '进口', tone: 'warn' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}

/** 详情页/表单里的完整说法，与 purchaseType 的短标签同源，避免两处各自硬编码。 */
export function purchaseTypeFull(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'domestic': return { label: '国内采购', tone: 'info' };
    case 'import': return { label: '进口采购', tone: 'warn' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}
export function chargeType(s: string): string {
  switch (s) {
    case 'iva': return 'IVA 增值税';
    case 'iva_perception': return 'IVA 预扣';
    case 'iibb_perception': return '收入总额预扣';
    case 'internal_tax': return '内部税';
    case 'customs_duty': return '关税';
    case 'statistical_fee': return '统计税';
    case 'international_freight': return '国际运费';
    case 'insurance': return '保险';
    case 'customs_broker': return '报关费';
    case 'local_freight': return '国内运费';
    case 'other': return '其他费用';
    default: return s || '—';
  }
}

// 退货 / 退料标签（阶段3 · P3-4/P3-5，与 core-api returns / return_lines CHECK 约束对齐）。
export function returnStatus(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'draft': return { label: '草稿', tone: 'neutral' };
    case 'authorized': return { label: '已授权', tone: 'info' };
    case 'received': return { label: '已收到退货', tone: 'info' };
    case 'inspected': return { label: '已检验', tone: 'warn' };
    case 'resolved': return { label: '已处理完成', tone: 'ok' };
    case 'cancelled': return { label: '已取消', tone: 'bad' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}
export function returnType(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'customer_return': return { label: '客户退货', tone: 'warn' };
    case 'supplier_material_return': return { label: '供应商退料', tone: 'info' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}
// 处理方式：库存方向的关键。restock/repair/replace/scrap 面向客户退货入库端；return_to_supplier 面向退料出库端。
export function returnDisposition(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'restock': return { label: '回库（良品入库）', tone: 'ok' };
    case 'repair': return { label: '返修', tone: 'warn' };
    case 'replace': return { label: '换货', tone: 'info' };
    case 'scrap': return { label: '报废', tone: 'bad' };
    case 'return_to_supplier': return { label: '退还供应商', tone: 'info' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}
export function inspectionResult(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'pending': return { label: '待检', tone: 'neutral' };
    case 'accepted': return { label: '合格', tone: 'ok' };
    case 'rejected': return { label: '不合格', tone: 'bad' };
    case 'conditional': return { label: '让步接收', tone: 'warn' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: '系统管理员',
  sales: '销售',
  production: '生产',
  warehouse: '仓储',
  quality: '质检',
  finance: '财务',
  viewer: '只读',
};
// 统一身份角色的中文名（方案 1）。ROLE_LABELS 只覆盖旧岗位角色，
// 统一身份账号登录后 role 是 company_admin/engineer/finance/customer，需单独命名，
// 否则界面上会直接显示英文角色名。
const IDENTITY_ROLE_LABELS: Record<string, string> = {
  company_admin: '公司管理员',
  engineer: '工程师',
  customer: '客户',
};

export function roleLabel(role?: string): string {
  if (!role) return '—';
  return IDENTITY_ROLE_LABELS[role] || ROLE_LABELS[role as Role] || role;
}

// 用于年度筛选下拉：当前年前后若干年。真实数据按记录日期字段过滤。
export function recentYears(back = 6): string[] {
  const cur = new Date().getFullYear();
  const years: string[] = [];
  for (let y = cur; y >= cur - back; y--) years.push(String(y));
  return years;
}

// ---- 财务标签（ARCA 税票 / 收款 / 附件）----
// 与 core-api 的 CHECK 约束逐一对应；未知值原样回显，绝不猜测成某个已知状态。
export function arcaInvoiceStatus(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'draft': return { label: '草稿', tone: 'neutral' };
    case 'authorized': return { label: '已授权', tone: 'ok' };
    case 'rejected': return { label: '被拒绝', tone: 'bad' };
    case 'cancelled': return { label: '已取消', tone: 'bad' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}

export function paymentStatus(s: string): { label: string; tone: Tone } {
  switch (s) {
    case 'draft': return { label: '草稿', tone: 'neutral' };
    case 'posted': return { label: '已登记', tone: 'ok' };
    case 'cancelled': return { label: '已取消', tone: 'bad' };
    default: return { label: s || '—', tone: 'neutral' };
  }
}

// 收款方式目前是自由文本（无受控字典），只对已知取值给出中文，其余原样显示。
export function paymentMethodLabel(m: string): string {
  switch (m) {
    case 'transfer': return '转账';
    case 'e_check': return '电子支票';
    case 'cash': return '现金';
    case 'check': return '支票';
    default: return m || '—';
  }
}

export function attachmentKindLabel(kind: string): string {
  switch (kind) {
    case 'arca_voucher': return 'ARCA 原票';
    case 'statement': return '对账单';
    case 'customer_po': return '客户 PO';
    case 'delivery_signature': return '交货签字';
    case 'contract_pdf': return '合同 PDF';
    case 'general': return '其他';
    default: return kind || '其他';
  }
}

export function bytesLabel(n: number | null | undefined): string {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return '—';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * 税务局票号：销售点-票号，如 00003-00000074。
 *
 * 合同页「关联单据」与「ARCA 税票」列表共用这一处，避免两处各自拼装而出现
 * `00003·72` 与 `00003-00000074` 并存（2026-10-29 由用户指出）。
 * 销售点补足 5 位、票号补足 8 位，与纸质票和 ARCA 门户一致；
 * 缺税务号时退回内部编号（如 INV-2026-00002），不显示半截号码。
 */
export function fiscalInvoiceNo(inv: {
  number?: string | null;
  point_of_sale?: string | number | null;
  voucher_number?: string | number | null;
}): string {
  const pos = String(inv.point_of_sale ?? '').trim();
  const nro = String(inv.voucher_number ?? '').trim();
  if (!pos && !nro) return String(inv.number ?? '—');
  const posOut = /^\d+$/.test(pos) ? pos.padStart(5, '0') : pos;
  const nroOut = /^\d+$/.test(nro) ? nro.padStart(8, '0') : nro;
  return `${posOut}-${nroOut}`;
}
